import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import { Logo } from "./Logo";
import { SearchBox } from "./SearchBox";
import { NotificationBell } from "./NotificationBell";
import { LogOut, LayoutDashboard, Menu } from "lucide-react";

const dashPath = { candidate: "/candidato", company: "/empresa", admin: "/admin" };

const LINKS = [
  { label: "Empregos", to: "/empregos" },
  { label: "Como funciona", hash: "#como-funciona" },
  { label: "Vantagens", hash: "#vantagens" },
  { label: "Preços", hash: "#precos" },
  { label: "A nossa visão", hash: "#visao" },
];

export const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);

  const goto = (link) => {
    setOpen(false);
    if (link.to) return navigate(link.to);
    if (pathname === "/") {
      const el = document.querySelector(link.hash);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
    }
    navigate(`/${link.hash}`);
  };

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-black/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between gap-4">
        <Link to="/" data-testid="nav-logo" className="shrink-0 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500">
          <Logo size="sm" />
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {LINKS.map((l) => {
            const Icon = l.icon;
            return (
              <button key={l.label} data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`} onClick={() => goto(l)}
                className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors inline-flex items-center gap-1.5">
                {Icon && <Icon className="w-4 h-4 text-slate-400" />}
                {l.label}
              </button>
            );
          })}
        </nav>

        {pathname !== "/" && pathname !== "/empregos" && !pathname.startsWith("/auth") && (
          <SearchBox variant="compact" className="hidden md:block flex-1 max-w-sm" />
        )}

        <div className="flex items-center gap-2">
          {user && user !== false ? (
            <>
              <NotificationBell />
              <Button data-testid="nav-dashboard-btn" variant="outline" size="sm" className="rounded-full"
                onClick={() => navigate(dashPath[user.role] || "/")}>
                <LayoutDashboard className="w-4 h-4 mr-1.5" /> Painel
              </Button>
              <Button data-testid="nav-logout-btn" variant="ghost" size="sm" className="rounded-full" onClick={logout}>
                <LogOut className="w-4 h-4" />
              </Button>
            </>
          ) : (
            <>
              <Button data-testid="nav-login-btn" variant="ghost" size="sm" className="rounded-full hidden sm:inline-flex" onClick={() => navigate("/auth")}>Entrar</Button>
              <Button data-testid="nav-register-btn" size="sm" className="rounded-full bg-slate-900 hover:bg-slate-800" onClick={() => navigate("/auth?tab=register")}>Registar</Button>
            </>
          )}

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button data-testid="nav-mobile-menu" variant="ghost" size="sm" className="lg:hidden rounded-full px-2">
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-80">
              <div className="mt-2 mb-8"><Logo size="sm" /></div>
              <nav className="flex flex-col gap-1">
                {LINKS.map((l) => {
                  const Icon = l.icon;
                  return (
                    <button key={l.label} data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`} onClick={() => goto(l)}
                      className="text-left text-base font-medium text-slate-800 hover:text-amber-600 py-3 border-b border-black/5 transition-colors inline-flex items-center gap-2">
                      {Icon && <Icon className="w-4 h-4 text-slate-400" />}
                      {l.label}
                    </button>
                  );
                })}
              </nav>
              {(!user || user === false) && (
                <div className="mt-8 flex flex-col gap-2">
                  <Button className="rounded-full bg-slate-900 hover:bg-slate-800" onClick={() => { setOpen(false); navigate("/auth?tab=register"); }}>
                    Criar conta grátis
                  </Button>
                  <Button variant="outline" className="rounded-full" onClick={() => { setOpen(false); navigate("/auth"); }}>Entrar</Button>
                </div>
              )}
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
